import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { useEffectOnce } from "react-use";
import { Layout } from "antd";
import { Box, Button, Snackbar, Alert, Typography } from "@mui/material";
import { getRdxSelectionMapper, getRdxActionMapper } from "rdx/utils/propsMapping";
import SessionClient from "util/SessionClient";
import HeaderLogoLarge from "components/Icons/Logos/HeaderLogoLarge";
import EnvelopeOutlineIcon from "components/Icons/EnvelopeOutlineIcon";
import useLoading from "hooks/useLoading";
import types from "rdx/modules/emailVerification/types";
import messages from "rdx/modules/emailVerification/messages";
import EditEmailInline from "./components/EditEmailInline";

import styles from "./styles";

type AlertMessageT = {
  visible?: boolean;
  message?: string;
  severity?: "success" | "info" | "warning" | "error";
  duration?: number;
};

type PropsT = {
  latestMessage: {
    payload?: { event?: string };
    message?: string;
  };
  alertMessage: AlertMessageT;
  resendVerificationEmailRequest: () => void;
  logout: () => void;
  clearAlertMessage: () => void;
};

const ctaButtonSx = {
  background: "linear-gradient(90deg, #f97316, #fb923c)",
  borderRadius: "50px",
  py: 1.75,
  px: 4,
  minWidth: 220,
  fontSize: "1rem",
  fontWeight: 700,
  fontFamily: "'Barlow', sans-serif",
  textTransform: "none",
  boxShadow: "0 4px 24px rgba(249,115,22,0.35)",
  mt: 1,
  "&:hover": { background: "linear-gradient(90deg, #ea6c0a, #f97316)" },
} as const;

const VerifyEmailRequired = ({
  latestMessage,
  alertMessage,
  resendVerificationEmailRequest,
  logout,
  clearAlertMessage,
}: PropsT) => {
  const history = useHistory();
  const session = new SessionClient();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "info" | "warning" | "error">("success");

  const isLoading = !!useLoading({
    watchRequests: [types.RESEND_VERIFICATION_EMAIL_REQUEST],
  });

  useEffectOnce(() => {
    if (!session.jwt) {
      history.replace("/login");
      return;
    }
    if (session.user?.props?.emailVerified === true) {
      history.replace("/dashboard");
    }
  });

  useEffect(() => {
    const event = latestMessage?.payload?.event;
    if (event === messages.RESEND_VERIFICATION_SUCCESS_EVENT) {
      setSnackbarMessage(messages.RESEND_VERIFICATION_SUCCESS);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    }
    if (event === messages.RESEND_VERIFICATION_ALREADY_VERIFIED_EVENT) {
      setSnackbarMessage("Your email is already verified.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setTimeout(() => {
        window.location.href = "/dashboard";
      }, 2000);
    }
  }, [latestMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleAlertClose = () => {
    clearAlertMessage();
  };

  const [displayEmail, setDisplayEmail] = useState<string | undefined>(session.user?.props?.email);
  const authFormat: string | undefined = session.user?.props?.authFormat;

  const handleEmailUpdateSuccess = (newEmail: string) => {
    session.setUserProps({ email: newEmail });
    setDisplayEmail(newEmail);
    setSnackbarMessage(`Verification email sent to ${newEmail}`);
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleLogout = () => {
    logout();
    if (session.user) {
      session.user.clearStore();
    }
    session.clear();
    window.location.href = "/login";
  };

  return (
    <Layout css={styles.pageLayout}>
      <Box sx={{ mb: 4, display: "flex", justifyContent: "center" }}>
        <HeaderLogoLarge width="146px" height="46px" />
      </Box>
      <div css={styles.confirmWindow}>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "55%",
            height: "55%",
            background: "radial-gradient(ellipse at top left, rgba(56,189,248,0.14) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            right: 0,
            width: "50%",
            height: "50%",
            background: "radial-gradient(ellipse at bottom right, rgba(32,194,241,0.10) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <div css={styles.verifyBody}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, textAlign: "center" }}>
            <EnvelopeOutlineIcon width={64} height={64} fill="rgba(255,255,255,0.5)" />
            <Typography variant="h5" sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: "white" }}>
              {"Verify your "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(90deg, #38bdf8 0%, #20C2F1 55%, #67e8f9 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                email
              </Box>
            </Typography>
            <Typography sx={{ fontFamily: "'Barlow', sans-serif", color: "rgba(255,255,255,0.82)", fontSize: 16 }}>
              Check your inbox for a verification link from Powur. Click the link to activate your account.
            </Typography>
            {displayEmail && (
              <Typography sx={{ fontFamily: "'Barlow', sans-serif", fontWeight: 600, color: "#20C2F1", fontSize: 14 }}>
                {displayEmail}
              </Typography>
            )}
            <EditEmailInline
              currentEmail={displayEmail ?? ""}
              authFormat={authFormat}
              onSuccess={handleEmailUpdateSuccess}
            />
            <Button
              variant="contained"
              onClick={() => resendVerificationEmailRequest()}
              disabled={isLoading}
              sx={ctaButtonSx}
            >
              Resend verification email
            </Button>
            <Button
              variant="text"
              onClick={handleLogout}
              sx={{
                fontFamily: "'Barlow', sans-serif",
                color: "rgba(255,255,255,0.5)",
                textTransform: "none",
                fontSize: 14,
                "&:hover": {
                  color: "#20C2F1",
                  backgroundColor: "transparent",
                },
              }}
            >
              Log out
            </Button>
          </Box>
        </div>
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!alertMessage?.visible && !!alertMessage?.message}
        autoHideDuration={alertMessage?.duration}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleAlertClose} severity={alertMessage?.severity}>
          {alertMessage?.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export { VerifyEmailRequired };

export default connect(
  getRdxSelectionMapper({
    latestMessage: "getLatestMessageEvt",
    alertMessage: "selectAlertMessage",
  }),
  getRdxActionMapper(["resendVerificationEmailRequest", "logout", "clearAlertMessage"]),
)(VerifyEmailRequired);
